import { schemaResolver, useForm } from '@mantine/form';
import { useEffect } from 'react';

import {
  type LoadInvoiceColumnVariablesInput,
  LoadInvoiceColumnVariablesSchema,
  useLoadInvoiceColumn,
} from './use-load-invoice-column';

interface UseLoadKanbanInvoicesOptions {
  initialFilters?: Partial<LoadInvoiceColumnVariablesInput>;
}

export function useLoadKanbanInvoices({ initialFilters }: UseLoadKanbanInvoicesOptions = {}) {
  const searchParams = useSearchParams();
  const filtersForm = useForm<LoadInvoiceColumnVariablesInput>({
    validate: schemaResolver(LoadInvoiceColumnVariablesSchema, { sync: true }),
    initialValues: {
      accountManagerId: null,
      column: 'late', // placholder for type correctness, overwritten below,
      debtorId: '',
      debtorName: '',
      endDate: null,
      startDate: null,
      maxDaysLate: '',
      minDaysLate: '',
      reference: '',
      unpaused: null,
      remainingBalance: {
        amount: '',
        operator: '<=',
      },
      ...initialFilters,
    },
  });

  useEffect(() => {
    const debtorName = searchParams.get('debtorName');
    if (debtorName) filtersForm.setFieldValue('debtorName', debtorName);
  }, []);

  const filters = filtersForm.getValues();

  const late = useLoadInvoiceColumn({ ...filters, column: 'late' });
  const firstReminder = useLoadInvoiceColumn({ ...filters, column: 'first_reminder' });
  const lastReminder = useLoadInvoiceColumn({ ...filters, column: 'last_reminder' });
  const formalNotice = useLoadInvoiceColumn({ ...filters, column: 'formal_notice' });
  const thirdPartyCase = useLoadInvoiceColumn({ ...filters, column: 'third_party_case' });
  const disputed = useLoadInvoiceColumn({ ...filters, column: 'disputed' });

  return {
    columns: {
      late,
      firstReminder,
      lastReminder,
      formalNotice,
      thirdPartyCase,
      disputed,
    },
    filtersForm,
  };
}

const useSearchParams = () => {
  const searchParams = new URLSearchParams(window.location.search);

  return {
    get: (key: string) => searchParams.get(key),
    set: (key: string, value: string) => {
      searchParams.set(key, value);
      window.history.replaceState(null, '', `?${searchParams.toString()}`);
    },
  };
};
